import { PageContextStory, Story } from '../build/types';
import { StoryblokService } from '../storyblok';
import { Breadcrumbs, Link, LinkMap, StoryblokLinkNode } from './types';
import { createMapFromId, encodeUrl, isExternalUrl, isHttpLink, isTypeAsset, isTypeUrl, mockLink } from './util';
import { maskAssetUrl } from '../../utils';

export const NavigationService = {
  rootAlias: process.env.GATSBY_HOME_STORY_SLUG,

  currentPageCssClass: 'current-page',

  getParsedMultiLink(link: Link): string {
    if (isExternalUrl(link)) {
      return encodeUrl(link);
    }

    const url = encodeUrl(link).replace(this.rootAlias || '', '');
    const anchor = link.anchor ? `#${link.anchor}` : '';

    return `/${url}${anchor}`.replace('//', '/').replace('/#', '#');
  },

  getParsedLink: (link: Link = mockLink): string => encodeURI(link.url || link.story?.url || ''),

  isCurrentPage: (link: Link): boolean =>
    `${global.window?.location.pathname}/` === `/${NavigationService.getParsedLink(link)}`,

  isWhitelistedDomain(url: string): boolean {
    try {
      const whitelist: string[] = JSON.parse(process.env.GATSBY_WHITE_LISTED_DOMAINS) || [];
      const { hostname } = new URL(url);

      return whitelist.some((domain: string) => hostname.endsWith(domain));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Invalid URL or whitelist', error);
      return false;
    }
  },

  getParsedNavigationLink(link: Link = mockLink): string {
    // If the Link points to an asset, we only have to mask the URL so we point to assets.roche.com.
    if (isTypeAsset(link)) {
      return maskAssetUrl(link.url);
    }

    if (isTypeUrl(link) && !isHttpLink(link)) {
      return `https://${encodeUrl(link)}`;
    }

    if (isTypeUrl(link)) {
      return encodeUrl(link);
    }

    return `/${encodeUrl(link)}`.replace('//', '/');
  },

  getCurrentPagePath(): string {
    const url = new URL(window.location.href);
    const { pathname } = url;

    const editorPath = url.searchParams.get('path') || '';
    return ['editor', '/editor', '/editor/'].indexOf(pathname) >= 0 ? editorPath : pathname;
  },

  async getMapAllLinks(): Promise<LinkMap> {
    const allLinks = await StoryblokService.getLinks('');
    return createMapFromId(allLinks);
  },

  async getLink(path: string): Promise<StoryblokLinkNode | undefined> {
    const links = await StoryblokService.getLinks(path);

    const sanitizedPath = path.startsWith('/') ? path : `/${path}`;

    return links.find((link: StoryblokLinkNode) => link.real_path === sanitizedPath);
  },

  async getBreadcrumbsFromId(
    currentId: number | string,
    allLinks: Breadcrumbs,
    allEntries: Story<PageContextStory>[],
  ): Promise<StoryblokLinkNode[]> {
    if (!allLinks[currentId]) {
      return [];
    }

    let { parent_id: parentId } = allLinks[currentId];
    const breadcrumbs = [allLinks[currentId]];

    while (parentId > 0) {
      const parentStory = allEntries.filter(
        (entry: Story<PageContextStory>) => entry.full_slug === `${allLinks[parentId].slug}/`,
      );
      if (parentStory.length === 0) {
        allLinks[parentId].no_link = true;
      }

      breadcrumbs.push(allLinks[parentId]);
      parentId = allLinks[parentId]?.parent_id;
    }

    return breadcrumbs.reverse();
  },

  async getBreadcrumbsFromPath(path: string) {
    const currentLink = await this.getLink(path);

    if (!currentLink?.real_path) {
      return [];
    }

    const links = currentLink.real_path.slice(1).split('/');

    const breadcrumbs = await Promise.all(
      links.map(async (link, index) => {
        const previous = [...links.slice(0, index), link];
        return this.getLink(previous.join('/'));
      }),
    );

    return breadcrumbs;
  },
};
